import { BRSR_BASE_PATH, BRSR_STEPS, MATURITY_SCALE_NOTE } from '../../config/brsrConfig'
import { questionsForStage } from '../../data/brsr/questions'
import type { BrsrQuestion, BrsrState } from '../../data/brsr/types'
import { trackAssessmentEvent } from '../../lib/analytics/events'
import { evaluateApplicability } from '../../lib/brsr/applicability'
import { computeBrsrResult } from '../../lib/brsr/engine'
import { saveBrsrState } from '../../lib/brsr/session'
import { validateStep } from '../../lib/brsr/validation'
import { isQuestionVisible } from '../../lib/brsr/visibility'
import { clear, el, errorBox, fieldLabel, helpText } from '../assessment/ui'

const STEP_ORDER = BRSR_STEPS.map((step) => step.id)

export function mountBrsrWizard(root: HTMLElement, state: BrsrState): void {
  trackAssessmentEvent('assessment_start', { tool: 'india-brsr-readiness' })
  render(root, state)
}

function render(root: HTMLElement, state: BrsrState): void {
  clear(root)
  const idx = Math.max(0, STEP_ORDER.indexOf(state.currentStep))
  const step = BRSR_STEPS[idx]
  const shell = el('div', { className: 'assess-shell' })
  const progress = el('div', { className: 'assess-progress', role: 'navigation', 'aria-label': 'Assessment progress' })
  const steps = el('ol', { className: 'assess-progress__steps' })
  BRSR_STEPS.forEach((item, index) => {
    const li = el('li', {
      className: 'assess-progress__step' + (index === idx ? ' is-current' : '') + (index < idx ? ' is-done' : ''),
    })
    const btn = el('button', {
      type: 'button',
      className: 'assess-progress__step-btn',
      'aria-label': `Go to step ${index + 1}: ${item.title}`,
      ...(index === idx ? { 'aria-current': 'step' } : {}),
    }) as HTMLButtonElement
    btn.append(el('span', { className: 'assess-progress__step-num' }, [String(index + 1)]), el('span', { className: 'assess-progress__step-label' }, [item.label]))
    btn.addEventListener('click', () => {
      if (index === idx) return
      state.currentStep = STEP_ORDER[index]
      saveBrsrState(state)
      render(root, state)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    })
    li.append(btn)
    steps.append(li)
  })
  const bar = el('div', { className: 'assess-progress__bar', 'aria-hidden': 'true' })
  const fill = el('div', { className: 'assess-progress__fill' })
  fill.style.width = `${((idx + 1) / BRSR_STEPS.length) * 100}%`
  bar.append(fill)
  progress.append(
    el('div', { className: 'assess-progress__meta' }, [
      el('strong', {}, [`Step ${idx + 1} of ${BRSR_STEPS.length}`]),
      el('span', {}, [`${step.title} · about ${step.estimateMin} min`]),
    ]),
    steps,
    bar,
  )

  const errorsHost = el('div', { id: 'brsr-errors' })
  const panel = el('div', { className: 'assess-panel', id: 'brsr-step-panel' })
  panel.append(el('h2', { className: 'assess-lp-h2' }, [step.title]), el('p', { className: 'assess-help' }, [step.intro]))
  if (step.id === 'disclosure' || step.id === 'esg_data' || step.id === 'evidence') {
    panel.append(el('p', { className: 'assess-scale-legend' }, [MATURITY_SCALE_NOTE]))
  }
  if (step.id === 'applicability') panel.append(renderApplicability(state))

  let section = ''
  for (const question of questionsForStage(step.id)) {
    if (!isQuestionVisible(question, state.responses)) continue
    if (question.section !== section) {
      section = question.section
      panel.append(el('h3', { className: 'brsr-section' }, [section]))
    }
    panel.append(renderQuestion(question, state, root))
  }

  if (step.id === 'evidence') panel.append(renderReview(state))

  const nav = el('div', { className: 'assess-nav' })
  if (idx > 0) {
    const back = el('button', { type: 'button', className: 'btn btn--secondary' }, ['Back']) as HTMLButtonElement
    back.addEventListener('click', () => {
      state.currentStep = STEP_ORDER[idx - 1]
      saveBrsrState(state)
      render(root, state)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    })
    nav.append(back)
  }
  const next = el('button', { type: 'button', className: 'btn btn--primary' }, [
    idx === STEP_ORDER.length - 1 ? 'Score assessment' : 'Continue',
  ]) as HTMLButtonElement
  next.addEventListener('click', () => {
    const errors =
      idx === STEP_ORDER.length - 1
        ? STEP_ORDER.flatMap((id) => validateStep(id, state.responses))
        : validateStep(step.id, state.responses)
    clear(errorsHost)
    if (errors.length) {
      errorsHost.append(errorBox(errors.slice(0, 6)))
      errorsHost.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      return
    }
    trackAssessmentEvent('assessment_step_complete', { tool: 'india-brsr-readiness', step: step.id })
    if (idx === STEP_ORDER.length - 1) {
      state.result = computeBrsrResult(state.responses)
      state.completed = true
      state.completedAt = state.result.assessedAt
      saveBrsrState(state)
      trackAssessmentEvent('assessment_complete', { tool: 'india-brsr-readiness' })
      window.location.href = `${BRSR_BASE_PATH}/results`
      return
    }
    state.currentStep = STEP_ORDER[idx + 1]
    saveBrsrState(state)
    render(root, state)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  })
  nav.append(next)
  shell.append(progress, errorsHost, panel, nav)
  root.append(shell)
}

function renderApplicability(state: BrsrState): HTMLElement {
  const assessment = evaluateApplicability(state.responses)
  const wrap = el('div', { className: 'brsr-conclusions' })
  wrap.append(
    el('p', { className: 'assess-help' }, [
      'This screen updates from your profile. It does not check NSE or BSE ranking files.',
    ]),
  )
  for (const item of assessment.conclusions) {
    const card = el('article', { className: 'brsr-conclusion' })
    card.append(
      el('p', { className: `brsr-status brsr-status--${item.status}` }, [statusText(item.status)]),
      el('h3', {}, [item.title]),
      el('p', {}, [item.conclusion]),
      el('p', { className: 'assess-help' }, [`${item.reason} Next step: ${item.nextStep}`]),
    )
    wrap.append(card)
  }
  return wrap
}

function renderReview(state: BrsrState): HTMLElement {
  const visible = questionsForStage('evidence')
    .concat(questionsForStage('disclosure'), questionsForStage('esg_data'))
    .filter((question) => question.required && isQuestionVisible(question, state.responses))
  const unanswered = visible.filter((question) => {
    const value = state.responses[question.id]
    return value == null || value === '' || (Array.isArray(value) && value.length === 0)
  })
  const unsure = visible.filter((question) => state.responses[question.id] === 'not_sure')
  const notApplicable = visible.filter((question) => state.responses[question.id] === 'not_applicable')
  const box = el('div', { className: 'assess-result-card' })
  box.append(el('h3', {}, ['Review before scoring']))
  box.append(
    el('p', {}, [
      `${unanswered.length} required questions on this path are still unanswered. ${unsure.length} are marked not sure. ${notApplicable.length} are marked not applicable. Not sure and not applicable will not increase the score.`,
    ]),
  )
  if (unanswered.length) {
    const list = el('ul', { className: 'brsr-review-list' })
    unanswered.slice(0, 8).forEach((question) => list.append(el('li', {}, [question.label])))
    box.append(list)
  }
  return box
}

function renderQuestion(question: BrsrQuestion, state: BrsrState, root: HTMLElement): HTMLElement {
  const field = el('fieldset', { className: 'assess-fieldset' })
  const legend = el('legend', { className: 'assess-label' }, [
    question.label,
    question.required ? el('span', { className: 'assess-required', 'aria-hidden': 'true' }, [' *']) : '',
  ])
  field.append(legend)
  if (question.helpText) field.append(helpText(question.helpText))

  if (question.kind === 'text') {
    const input = el('input', {
      className: 'assess-input',
      id: question.id,
      type: 'text',
      name: question.id,
      autocomplete: 'organization',
      ...(question.required ? { required: true } : {}),
    }) as HTMLInputElement
    const current = state.responses[question.id]
    input.value = typeof current === 'string' ? current : ''
    if (question.id !== 'company_name') input.removeAttribute('autocomplete')
    input.addEventListener('input', () => {
      state.responses[question.id] = input.value
      state.completed = false
      state.result = null
      saveBrsrState(state)
    })
    const label = fieldLabel(question.label, Boolean(question.required), question.id)
    label.classList.add('brsr-sr-legend')
    const wrap = el('div', { className: 'assess-field' })
    wrap.append(label, question.helpText ? helpText(question.helpText) : '', input)
    return wrap
  }

  if (question.kind === 'multi') {
    const selected = new Set(Array.isArray(state.responses[question.id]) ? (state.responses[question.id] as string[]) : [])
    const list = el('div', { className: 'assess-option-list', role: 'group', 'aria-label': question.label })
    for (const option of question.options ?? []) {
      const id = `${question.id}-${option.value}`
      const checkbox = el('input', { type: 'checkbox', className: 'assess-option__input', id, name: question.id, value: option.value }) as HTMLInputElement
      checkbox.checked = selected.has(option.value)
      checkbox.addEventListener('change', () => {
        const next = new Set(Array.isArray(state.responses[question.id]) ? (state.responses[question.id] as string[]) : [])
        if (checkbox.checked) next.add(option.value)
        else next.delete(option.value)
        state.responses[question.id] = [...next]
        state.completed = false
        state.result = null
        saveBrsrState(state)
        render(root, state)
      })
      const choice = el('label', { className: 'assess-option assess-option--check' + (checkbox.checked ? ' is-selected' : ''), for: id })
      choice.append(checkbox, el('span', { className: 'assess-option__control', 'aria-hidden': 'true' }), el('span', { className: 'assess-option__text' }, [option.label]))
      list.append(choice)
    }
    field.append(list)
    return field
  }

  const current = typeof state.responses[question.id] === 'string' ? state.responses[question.id] : ''
  const list = el('div', { className: 'assess-option-list', role: 'radiogroup', 'aria-label': question.label })
  for (const option of question.options ?? []) {
    const id = `${question.id}-${option.value}`
    const radio = el('input', { type: 'radio', className: 'assess-option__input', id, name: question.id, value: option.value }) as HTMLInputElement
    radio.checked = current === option.value
    radio.addEventListener('change', () => {
      state.responses[question.id] = option.value
      state.completed = false
      state.result = null
      saveBrsrState(state)
      render(root, state)
    })
    const choice = el('label', { className: 'assess-option' + (radio.checked ? ' is-selected' : ''), for: id })
    choice.append(radio, el('span', { className: 'assess-option__control', 'aria-hidden': 'true' }), el('span', { className: 'assess-option__text' }, [option.label]))
    list.append(choice)
  }
  field.append(list)
  return field
}

function statusText(status: string): string {
  if (status === 'verified_rule') return 'Verified rule'
  if (status === 'conditional') return 'Conditional conclusion'
  return 'Requires verification'
}
